import { useState, useMemo } from 'react';
import { SurveySection, PaginationConfig, SectionData, PaginationState, SurveyPaginationProps } from '../types';

const DEFAULT_PAGE = 1;

function shouldPaginateSection(section: SectionData | null, isMobile: boolean, defaultPerPage: number): boolean {
	if (!section) return false;
	const { type, questions } = section;
	const overLimit = type === SurveySection.RADIO && questions.length > defaultPerPage;
	const mobilePaginate = isMobile && (type === SurveySection.SLIDER || type === SurveySection.WORKLOAD);
	return overLimit || mobilePaginate;
}

function getItemsPerPage(section: SectionData | null, defaultPerPage: number, isMobile: boolean): number {
	return section && isMobile && (section.type === SurveySection.SLIDER || section.type === SurveySection.WORKLOAD)
		? DEFAULT_PAGE
		: defaultPerPage;
}

function calculateTotalPages(sections: SectionData[], isMobile: boolean, defaultPerPage: number): number {
	return sections.reduce((sum, sec) => {
		const paginate = shouldPaginateSection(sec, isMobile, defaultPerPage);
		if (!paginate) return sum + DEFAULT_PAGE;
		const itemsPerPage = getItemsPerPage(sec, defaultPerPage, isMobile);
		return sum + Math.ceil(sec.questions.length / itemsPerPage);
	}, 0);
}

function findSectionAndPage(
	globalPage: number,
	sections: SectionData[],
	defaultPerPage: number,
	isMobile: boolean
): { sectionIndex: number; pageInSection: number } {
	if (!sections.length || globalPage < DEFAULT_PAGE) return { sectionIndex: 0, pageInSection: DEFAULT_PAGE };

	let accumulated = DEFAULT_PAGE;
	for (let i = 0; i < sections.length; i++) {
		const sec = sections[i];
		const paginate = shouldPaginateSection(sec, isMobile, defaultPerPage);
		const perPage = paginate ? getItemsPerPage(sec, defaultPerPage, isMobile) : sec.questions.length;
		const pages = Math.ceil(sec.questions.length / perPage);

		if (globalPage >= accumulated && globalPage < accumulated + pages) {
			return {
				sectionIndex: i,
				pageInSection: paginate ? globalPage - accumulated + DEFAULT_PAGE : DEFAULT_PAGE
			};
		}
		accumulated += pages;
	}
	return { sectionIndex: sections.length - DEFAULT_PAGE, pageInSection: DEFAULT_PAGE };
}

function getPaginationState(
	currentPage: number,
	sections: SectionData[],
	isMobile: boolean,
	defaultPerPage: number
): PaginationState {
	const { sectionIndex, pageInSection } = findSectionAndPage(currentPage, sections, defaultPerPage, isMobile);
	const currentSec = sections[sectionIndex] || null;
	const totalPages = calculateTotalPages(sections, isMobile, defaultPerPage);
	const paginate = shouldPaginateSection(currentSec, isMobile, defaultPerPage);
	const perPage = paginate ? getItemsPerPage(currentSec, defaultPerPage, isMobile) : defaultPerPage;

	const currentQuestions = currentSec
		? paginate
			? currentSec.questions.slice((pageInSection - DEFAULT_PAGE) * perPage, pageInSection * perPage)
			: currentSec.questions
		: [];

	return {
		isLastPage: currentPage >= totalPages,
		currentPageInSection: pageInSection,
		currentSectionIndex: sectionIndex,
		currentSection: currentSec,
		currentQuestions,
		totalPages
	};
}

export function useSurveyPagination({
	workloadQuestions,
	questionsPerPage,
	sliderQuestions,
	radioQuestions,
	isMobile
}: SurveyPaginationProps): PaginationConfig {
	const sections = useMemo(() => {
		const arr: SectionData[] = [];
		if (radioQuestions.length) arr.push({ type: SurveySection.RADIO, questions: radioQuestions });
		if (sliderQuestions.length) arr.push({ type: SurveySection.SLIDER, questions: sliderQuestions });
		if (workloadQuestions.length) arr.push({ type: SurveySection.WORKLOAD, questions: workloadQuestions });
		return arr;
	}, [radioQuestions, sliderQuestions, workloadQuestions]);

	const [currentPage, setCurrentPage] = useState(DEFAULT_PAGE);
	const pagination = useMemo(
		() => getPaginationState(currentPage, sections, isMobile, questionsPerPage),
		[currentPage, sections, isMobile, questionsPerPage]
	);

	const handleNext = () => currentPage < pagination.totalPages && setCurrentPage((p) => p + DEFAULT_PAGE);
	const handleBack = () => currentPage > DEFAULT_PAGE && setCurrentPage((p) => p - DEFAULT_PAGE);
	const goToPage = (page: number, secIndex?: number) => {
		window.scrollTo(0, 0);
		if (secIndex !== undefined) {
			let target = sections
				.slice(0, secIndex)
				.reduce(
					(sum, sec) =>
						sum +
						(shouldPaginateSection(sec, isMobile, questionsPerPage)
							? Math.ceil(sec.questions.length / getItemsPerPage(sec, questionsPerPage, isMobile))
							: DEFAULT_PAGE),
					DEFAULT_PAGE
				);
			if (page > DEFAULT_PAGE && secIndex < sections.length) {
				const sec = sections[secIndex];
				if (shouldPaginateSection(sec, isMobile, questionsPerPage)) {
					const maxPage = Math.ceil(sec.questions.length / getItemsPerPage(sec, questionsPerPage, isMobile));
					target += Math.min(page, maxPage) - DEFAULT_PAGE;
				}
			}
			if (target >= DEFAULT_PAGE && target <= pagination.totalPages) setCurrentPage(target);
		} else if (page >= DEFAULT_PAGE && page <= pagination.totalPages) {
			setCurrentPage(page);
		}
	};

	return {
		currentSection: pagination.currentSection?.type ?? SurveySection.RADIO,
		getCurrentQuestions: () => pagination.currentQuestions,
		hasNext: () => currentPage < pagination.totalPages,
		getTotalPages: () => pagination.totalPages,
		isLastSection: pagination.isLastPage,
		hasBack: () => currentPage > DEFAULT_PAGE,
		currentPage,
		handleNext,
		handleBack,
		goToPage
	};
}
